import { Box, Paper, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useCurrency } from "../../context/CurrencyContext";
import { usePortfolio } from "../../context/PortfolioContext";
import { percentage } from "../../shared/percentage";
import {
  PartyValueInCase,
  ValueStatistics,
} from "../../shared/ValueStatistics";
import CumulativeValueAtRiskChart, {
  ValueAtRiskLegend,
} from "../analytics/CumulativeValueAtRiskChart";
import { histogram, monteCarlo } from "../../shared/MonteCarlo";
import { SectionLabel } from "../general/SectionLabel";
import { SegmentedControl } from "../general/SegmentedControl";
import { extremeFigure, FigureCard } from "./ClientsPortfolioParts";
import { HistogramChart } from "./HistogramChart";
import { InteractiveBar, LinkedBarChart } from "./LinkedBarChart";
import { card, cardRow, SectionHeading } from "./PortfolioSection";
import QuantumClientsPortfolio from "./QuantumClientsPortfolio";

/** What the chart of the cases shows of each case. */
type CaseValue = "ev" | "netResult";

/** The chart of the cases keeps this width on a narrower page, in px. */
const CASE_CHART_MIN_WIDTH = 760;

/** The win chances are counted in ranges of five points. */
const WIN_CHANCE_BINS = 20;

/** The height of the chart of the win chances, in px. */
const WIN_CHANCE_CHART_HEIGHT = 230;

/**
 * The value at risk is simulated: the portfolio is drawn this many times, and
 * the results are gathered into this many values.
 */
const SIMULATIONS = 10000;
const SIMULATED_VALUES = 200;

const headerRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  columnGap: 1.5,
  rowGap: 1,
} as const;

const description = { mt: 0.5, color: "text.secondary" } as const;

const caption = {
  display: "block",
  textAlign: "center",
  color: "text.secondary",
} as const;

/**
 * What the cases are worth to the clients: their expected values and net
 * results, the amounts claimed, the chances of winning, and the value at risk
 * of them all taken together.
 */
export const ClientsPortfolio = () => {
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "portfolio" });
  const { search } = useLocation();
  const {
    evs,
    clients,
    winChance,
    numberOfCases,
    netResults,
    caseNameById,
    fullySettledCaseIds,
  } = usePortfolio();

  const [caseValue, setCaseValue] = useState<CaseValue>("ev");

  const fullySettledCases = useMemo(
    () => new Set(fullySettledCaseIds),
    [fullySettledCaseIds],
  );

  const caseValueChartData: InteractiveBar[] = useMemo(() => {
    const data: InteractiveBar[] = [];
    Object.values(clients.stats).forEach((clientCases) =>
      Object.entries(clientCases).forEach(([caseId, { stats }]) => {
        // the EV of a fully settled case is an artefact of there being no
        // live dispute left, not a figure the case was ever worth; its net
        // result (the settlement) still is, so only the EV view leaves
        // those cases out
        if (caseValue === "ev" && fullySettledCases.has(caseId)) return;
        const value = caseValue === "ev" ? stats.ev : stats.netResult?.average;
        if (value === undefined) return;

        const label = caseNameById[caseId] ?? caseId;
        data.push({ label, value, href: `/cases/${caseId}` });
      }),
    );
    return data.sort((a, b) => a.value - b.value);
  }, [clients.stats, caseNameById, caseValue, fullySettledCases]);

  // the simulation draws at random: run with every render, it would redraw
  // the curve whenever anything on the page changes
  const valueAtRisk = useMemo(
    () =>
      histogram(
        monteCarlo(clients.outcomes, SIMULATIONS),
        SIMULATED_VALUES,
      ).filter(({ probability }) => probability > 0),
    [clients.outcomes],
  );

  const named =
    (key: string) =>
    ({ partyName, caseName }: PartyValueInCase) =>
      t(key, { partyName, caseName });

  /** The total, the average and the two ends of a value of the cases. */
  const values = (
    key: "evs" | "netResults",
    title: string,
    { total, avg, max, min }: ValueStatistics,
    page: string,
  ) => (
    <FigureCard
      title={title}
      figures={[
        {
          label: t(`${key}.cards.total.title`),
          value: currency.format(total),
          note: t(`${key}.cards.total.subTitle`),
        },
        {
          label: t(`${key}.cards.average.title`),
          value: currency.format(avg),
          note: t(`${key}.cards.average.subTitle`),
        },
        extremeFigure(max, {
          label: t(`${key}.cards.max.title`),
          tone: "favourable",
          format: currency.format,
          who: named(`${key}.cards.max.subTitle`),
          page,
        }),
        extremeFigure(min, {
          label: t(`${key}.cards.min.title`),
          tone: "adverse",
          format: currency.format,
          who: named(`${key}.cards.min.subTitle`),
          page,
        }),
      ]}
    />
  );

  return (
    <Stack spacing={2.25} useFlexGap sx={{ userSelect: "none" }}>
      <SectionHeading id="caseValues">{t("caseValues.title")}</SectionHeading>
      <Box sx={cardRow}>
        {values("evs", t("evs.evSummary"), evs, "analytics/value")}
        {values(
          "netResults",
          t("netResults.netResultSummary"),
          netResults,
          "analytics/netResults",
        )}
      </Box>

      {/* the cases one by one, the lowest first: each bar leads to its case */}
      <Paper component="section" variant="outlined" sx={card}>
        <Box sx={headerRow}>
          <SectionLabel component="h3">{t("valueChart.title")}</SectionLabel>
          <SegmentedControl
            ariaLabel={t("valueChart.shown")}
            value={caseValue}
            options={[
              { value: "ev", label: t("valueChart.ev") },
              { value: "netResult", label: t("valueChart.netResult") },
            ]}
            onChange={setCaseValue}
          />
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: CASE_CHART_MIN_WIDTH, mt: 2.75 }}>
            <LinkedBarChart data={caseValueChartData} />
            <Typography variant="caption" sx={{ ...caption, pt: 1 }}>
              {t(`valueChart.caption.${caseValue}`)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <QuantumClientsPortfolio />

      <SectionHeading id="winChances" rule>
        {t("winChances.title")}
      </SectionHeading>
      {/* the distribution beside its figures where the page has the room:
          at its full width the chart is nearly twice as wide as they are */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "stretch",
          gap: 2.25,
        }}
      >
        <Paper
          component="section"
          variant="outlined"
          sx={{ ...card, pb: 2, flex: "3 1 440px", minWidth: 0 }}
        >
          <SectionLabel component="h3">
            {t("winChances.histogram.title")}
          </SectionLabel>
          <Typography variant="body2" sx={description}>
            {t("winChances.histogram.description")}
          </Typography>
          <Box sx={{ height: WIN_CHANCE_CHART_HEIGHT, mt: 2.25 }}>
            <HistogramChart
              data={Object.values(clients.stats)
                .flatMap((clientCases) => Object.entries(clientCases))
                .filter(([caseId]) => !fullySettledCases.has(caseId))
                .map(
                  ([, { stats }]) => 1 - stats.probabilityOfNegativeJudgement,
                )}
              label="portfolio.winChances.histogram.label"
              tooltip="portfolio.winChances.histogram.tooltip"
              bins={WIN_CHANCE_BINS}
            />
          </Box>
          <Typography variant="caption" sx={{ ...caption, mt: 0.5 }}>
            {t("winChances.histogram.caption")}
          </Typography>
        </Paper>

        <FigureCard
          stacked
          title={t("winChances.winChanceSummary")}
          figures={[
            {
              label: t("analytics.cards.average.title"),
              value: percentage.format(winChance.avg),
              note: t("analytics.cards.average.subTitle", {
                count: numberOfCases,
              }),
            },
            extremeFigure(winChance.max, {
              label: t("analytics.cards.max.title"),
              tone: "favourable",
              format: percentage.format,
              who: named("analytics.cards.max.subTitle"),
              page: `analytics/outcomes${search}`,
            }),
            extremeFigure(winChance.min, {
              label: t("analytics.cards.min.title"),
              tone: "adverse",
              format: percentage.format,
              who: named("analytics.cards.min.subTitle"),
              page: `analytics/outcomes${search}`,
            }),
          ]}
          sx={{ flex: "1 1 280px", minWidth: 0 }}
        />
      </Box>

      <SectionHeading id="var" rule>
        {t("var.title")}
      </SectionHeading>
      <Paper component="section" variant="outlined" sx={{ ...card, pb: 2 }}>
        <Box sx={headerRow}>
          <SectionLabel component="h3">{t("var.title")}</SectionLabel>
          <ValueAtRiskLegend />
        </Box>
        <Typography variant="body2" sx={description}>
          {t("var.description")}
        </Typography>
        <Box sx={{ mt: 2.25 }}>
          <CumulativeValueAtRiskChart data={valueAtRisk} />
        </Box>
      </Paper>
    </Stack>
  );
};
